import { useState, useEffect } from "react";

import { useQuery, useMutation } from "@apollo/react-hooks";
import { GetVideoById } from "./queries";
import { EditVideo, DeleteVideo } from "./mutations";

import { getError } from "./helpers";

import UploadHelper from "../../helpers/upload";

export function useVideo({ setError, id }) {
  const { data, error: queryError } = useQuery(GetVideoById, {
    variables: { video: { id: id } }
  });

  useEffect(() => {
    if (queryError || (data && data.videos && !data.videos[0])) {
      setError(getError());
    }
  }, [data, queryError]);

  return {
    video: data && data.videos ? data.videos[0] : null
  };
}

export function useSaveVideo({
  error,
  name,
  video,
  thumbnail,
  thumbnailFile,
  videoDate,
  setError,
  onDismiss,
  refetchVideos
}) {
  const [isSaving, setIsSaving] = useState();
  const [editVideo] = useMutation(EditVideo, {
    refetchQueries: [
      { query: GetVideoById, variables: { video: { id: video && video.id } } }
    ]
  });

  const onSave = async () => {
    setError();
    setIsSaving(true);
    try {
      if (thumbnailFile) {
        const { signedUrl } = await UploadHelper.getSignedUrl(
          UploadHelper.THUMBNAIL,
          { key: video.wowzaId, contentType: thumbnailFile.type }
        );
        await UploadHelper.uploadToS3(signedUrl, thumbnailFile);
      }

      if (name !== video.name || videoDate !== video.scheduleStartTime) {
        await editVideo({
          variables: {
            video: { id: video && video.id, name, scheduleStartTime: videoDate }
          }
        });
        await refetchVideos();
      }

      onDismiss();
    } catch (err) {
      setError(getError());
    }
    setIsSaving(false);
  };

  return {
    isSaving,
    isSaveDisabled:
      (error && !video) ||
      (!thumbnail &&
        (!name || name === video.name) &&
        (!videoDate || videoDate === video.scheduleStartTime)),
    setIsSaving,
    onSave
  };
}

export function useDeleteVideo({ video, onDismiss, refetchVideos, setError }) {
  const [isDeleting, setIsDeleting] = useState();
  const [isAlertOpen, setIsAlertOpen] = useState();
  const [deleteVideo] = useMutation(DeleteVideo);

  const onDeletePressed = () => {
    setIsAlertOpen(true);
  };
  const onAlertCancel = () => setIsAlertOpen(false);

  const onDeleteConfirmed = async () => {
    setError();
    setIsAlertOpen(false);
    setIsDeleting(true);

    try {
      await deleteVideo({ variables: { id: video.id } });
      await refetchVideos();
      onDismiss();
    } catch (err) {
      setError(getError());
    }

    setIsDeleting(false);
  };

  return {
    isDeleting,
    isAlertOpen,
    isDeleteDisabled: isDeleting || !video,
    setIsDeleting,
    setIsAlertOpen,
    onDeletePressed,
    onDeleteConfirmed,
    onAlertCancel
  };
}
